import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { setI18nLanguage } from '../../i18n/config';
import i18n from '../../i18n/config';

// NOTE: AddUser / UserList / BankDetails / BankList / Profile are no longer
// imported or rendered here. AdminDashboard used to keep its own `view` and
// `editingProfile` state and render those components locally — that's what
// caused the bug where Edit Profile did nothing while on Add User / User
// List / Add Bank / Bank List: the `if (view === '...')` checks always
// returned before the `if (editingProfile)` check further down ever ran.
//
// Now all four sub-pages (plus Profile) are ordinary entries in App.js's
// pageStack, exactly like Contributions / Reports / Timeline. AdminDashboard
// just asks App.js to navigate via props — it doesn't render those screens
// itself anymore, so there's no ordering to get wrong.
const AdminDashboard = ({
  ngo,
  onHome,
  onBack,
  onAddExpenses,
  onReports,
  onAddUser,
  onUserList,
  onAddBank,
  onBankList,
  onEditProfile,
  user,
  onLogout,
}) => {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);

  // ---- Language menu state ----
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);

  // Close the profile dropdown on outside click — same pattern used on
  // AddUser / UserList / BankDetails / BankList.
  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  // Close the language menu on outside click.
  useEffect(() => {
    if (!langMenuOpen) return;
    const handleClickOutside = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const handleEditProfile = () => {
    setShowProfileMenu(false);
    if (onEditProfile) onEditProfile();
  };

  const handleLogout = () => {
    setShowProfileMenu(false);
    if (onLogout) onLogout();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar — DESIGN.md §2 (Pattern A: py-4 / gap-3 / flex-1 spacer) */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        {/* Home icon — 20x20, filled house glyph */}
        <button onClick={onHome} className="text-blue-600 hover:text-blue-700 flex-shrink-0" aria-label="Home" title="Home">
          <svg width="20" height="20" viewBox="0 0 24 24" className="fill-current">
            <path d="M12 3.2 3 10v10h6v-6h6v6h6V10l-9-6.8Z" />
          </svg>
        </button>

        {/* Back arrow — 22x22, outlined */}
        <button onClick={onBack} aria-label="Back" className="text-blue-600 hover:text-blue-700">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 12H5M5 12l7-7M5 12l7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Top-bar label */}
        <span className="text-sm font-medium text-gray-900 flex-1">{t('title')}</span>

        <div className="flex-1" />

        {/* ---- LANGUAGE TOGGLE ---- */}
        <div ref={langMenuRef} className="relative">
          <button
            onClick={() => setLangMenuOpen((open) => !open)}
            aria-label="Language menu"
            aria-expanded={langMenuOpen}
            className="text-xs font-medium text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 transition"
          >
            {i18n.language === 'ml' ? 'മലയാളം' : 'EN'}
          </button>
          {langMenuOpen && (
            <div className="absolute right-0 top-10 w-32 bg-white rounded-xl shadow-lg border border-gray-100 p-2 z-50">
              <button
                onClick={() => {
                  const lang = 'en';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'en' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                English
              </button>
              <button
                onClick={() => {
                  const lang = 'ml';
                  setI18nLanguage(lang);
                  localStorage.setItem('zbookkeeping_language', lang);
                  setLangMenuOpen(false);
                }}
                className={`w-full text-left rounded-lg px-3 py-2 text-sm transition ${
                  i18n.language === 'ml' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                മലയാളം
              </button>
            </div>
          )}
        </div>

        {/* ---- USER AVATAR + PROFILE DROPDOWN — DESIGN.md §3 ---- */}
        <div ref={profileRef} className="relative">
          <button
            onClick={() => setShowProfileMenu((open) => !open)}
            aria-label="Account menu"
            aria-expanded={showProfileMenu}
            className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-medium"
          >
            {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
          </button>

          {showProfileMenu && (
            <div className="absolute right-0 top-11 w-64 bg-white rounded-xl shadow-lg border border-gray-100 p-4 z-50">
              <p className="text-sm text-gray-900 truncate">{user?.name || user?.email}</p>
              <p className="text-sm text-gray-400 truncate mb-4">{user?.email}</p>

              <button
                onClick={handleEditProfile}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition mb-2"
              >
                {tc('editProfile')}
              </button>
              <button
                onClick={handleLogout}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium transition"
              >
                {tc('logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Body heading — DESIGN.md §2: centered, blue, uppercase, tracking-wider */}
      <div className="max-w-sm mx-auto px-4 py-8">
        <h1 className="text-center text-base font-medium text-blue-600 mb-6 uppercase tracking-wider">
          {t('title')}
        </h1>

        <div className="flex flex-col gap-4">

          {/* User Management */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">{t('userManagement')}</p>
            <div className="flex flex-col gap-2">
              <button onClick={onAddUser}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition">
                {t('addUser')}
              </button>
              <button onClick={onUserList}
                className="w-full bg-red-500 hover:bg-red-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition">
                {t('deleteUser')}
              </button>
            </div>
          </div>

          {/* Transactions */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">{t('transactions')}</p>
            <button onClick={onAddExpenses}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition">
              {t('addExpense')}
            </button>
          </div>

          {/* Reports */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">{t('reports')}</p>
            <button onClick={onReports}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition">
              {t('generateReports')}
            </button>
          </div>

          {/* Bank Details */}
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">{t('bankDetails')}</p>
            <div className="flex flex-col gap-2">
              <button onClick={onAddBank}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition">
                {t('addBankAc')}
              </button>
              <button onClick={onBankList}
                className="w-full bg-red-500 hover:bg-red-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium transition">
                {t('deleteBankAc')}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;